import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";
import { FieldWarningIndicator } from "routes/Customer/Waste/Manifests/FieldWarningIndicator";
import { SOURCE_LABEL, SOURCE_ORDER } from "routes/Customer/Waste/Manifests/manifestSourceDisplay";

import type { ReviewFieldDiff } from "./reviewFieldDiffs";

/**
 * In-input end adornment for a review field whose sources disagree: the shared
 * warning icon, stating what each source holds.
 *
 * The icon explains; it does not act. Taking a value happens in the offered-value
 * chips under the focused field — the same control the extraction review grid
 * uses, and the same keystroke. This tooltip used to offer copy-to-clipboard, so
 * the reviewer read a value here and typed it back into the field two inches
 * below; the chips write it.
 *
 * Rendered as a field `endAdornment` (inside the input) rather than beside it, so
 * it never reflows the surrounding row or steals width from narrow fields (e.g.
 * the state picker) and coexists cleanly with a validation error's helperText.
 * Returns `undefined` when there is no diff, so callers can pass the result
 * straight through as `endAdornment`.
 */
export function reviewDiffAdornment(diff: ReviewFieldDiff | undefined): ReactNode {
  if (!diff) return undefined;
  return (
    <FieldWarningIndicator
      tooltip={
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
            Sources disagree on this value
          </Typography>
          {SOURCE_ORDER.map((source) => {
            const value = diff.values[source];
            if (value == null || value === "") return null;
            return (
              <Typography key={source} variant="caption" sx={{ display: "block" }}>
                {SOURCE_LABEL[source]}: {value}
              </Typography>
            );
          })}
        </Box>
      }
      ariaLabel="Sources disagree on this value"
    />
  );
}
